import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Stethoscope, FlaskConical, ShieldCheck, Globe2, ArrowRight } from "lucide-react";
import { EASE } from "./Reveal";
import SectionLink from "./SectionLink";

const HERO_MAIN =
  "https://static.prod-images.emergentagent.com/jobs/9444b1cb-2cbd-41d7-b086-be1e8cd6725c/images/4fb15ce5eaa6d3df2d85a543c40cce8eeb3edf7bc67c174c283e868dbe9c2bd8.jpeg";
const HERO_SIDE =
  "https://customer-assets-wrfwihn1.emergentagent.net/job_premium-formulation/artifacts/uhhvgqpy_Picture%201.png";

const BADGE_ICON_SIZE = 18;
const CTA_ICON_SIZE = 16;

const FADE_HIDDEN = { opacity: 0 };
const FADE_VISIBLE = { opacity: 1 };
const RISE_HIDDEN = { opacity: 0, y: 24 };
const RISE_VISIBLE = { opacity: 1, y: 0 };
const LINE_HIDDEN = { y: "115%" };
const LINE_VISIBLE = { y: 0 };
const MAIN_IMG_HIDDEN = { opacity: 0, y: 60 };
const SIDE_IMG_HIDDEN = { opacity: 0, y: 40 };
const IMG_VISIBLE = { opacity: 1, y: 0 };

const LINE_STAGGER_S = 0.14;
const LINE_TRANSITIONS = [0, 1, 2].map((i) => ({ duration: 1.1, delay: 0.25 + i * LINE_STAGGER_S, ease: EASE }));
const CTA_TRANSITION = { duration: 0.9, delay: 1, ease: EASE };
const BADGES_TRANSITION = { duration: 1, delay: 1.25 };
const MAIN_IMG_TRANSITION = { duration: 1.2, delay: 0.5, ease: EASE };
const SIDE_IMG_TRANSITION = { duration: 1.2, delay: 0.75, ease: EASE };
const OVERLINE_TRANSITION = { duration: 1, delay: 0.1 };
const SUBTITLE_TRANSITION = { duration: 0.9, delay: 0.85, ease: EASE };

const badges = [
  { icon: Stethoscope, label: "Doctor Led" },
  { icon: FlaskConical, label: "Science Backed" },
  { icon: ShieldCheck, label: "NDA Protected" },
  { icon: Globe2, label: "Export Ready" },
];

const lines = [
  { id: "line-build", content: <>Build Exceptional</> },
  { id: "line-launch", content: <>Products. Launch</> },
  {
    id: "line-confidence",
    content: (
      <>
        with <em className="font-display italic font-medium">Confidence.</em>
      </>
    ),
  },
];

const HeroHeadline = () => (
  <h1 data-testid="hero-headline" className="font-display text-navy text-5xl sm:text-6xl lg:text-[4.6rem] leading-[1.04] tracking-tight">
    {lines.map(({ id, content }, i) => (
      <span key={id} className="block overflow-hidden pb-1">
        <motion.span
          className="block"
          initial={LINE_HIDDEN}
          animate={LINE_VISIBLE}
          transition={LINE_TRANSITIONS[i]}
        >
          {content}
        </motion.span>
      </span>
    ))}
  </h1>
);

const HeroCtas = ({ onContact }) => (
  <motion.div
    initial={RISE_HIDDEN}
    animate={RISE_VISIBLE}
    transition={CTA_TRANSITION}
    className="mt-10 flex flex-wrap items-center gap-4"
  >
    <button
      data-testid="hero-primary-cta"
      onClick={onContact}
      className="group btn-shine inline-flex items-center gap-3 rounded-full bg-navy text-ivory px-8 py-4 text-sm font-semibold tracking-wide transition-transform duration-300 hover:-translate-y-0.5"
    >
      Book a Free Call
      <ArrowRight size={CTA_ICON_SIZE} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
    </button>
    <SectionLink
      id="services"
      data-testid="hero-secondary-cta"
      className="inline-flex items-center gap-3 rounded-full border border-navy/25 text-navy px-8 py-4 text-sm font-semibold tracking-wide transition-colors duration-300 hover:bg-navy/5"
    >
      Explore Our Services
    </SectionLink>
  </motion.div>
);

const HeroBadges = () => (
  <motion.div
    initial={FADE_HIDDEN}
    animate={FADE_VISIBLE}
    transition={BADGES_TRANSITION}
    className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-y-6 max-w-xl border-t border-navy/10 pt-8"
    data-testid="hero-trust-badges"
  >
    {badges.map(({ icon: Icon, label }) => (
      <div key={label} className="flex items-center gap-2.5">
        <Icon size={BADGE_ICON_SIZE} strokeWidth={1.5} className="text-navy/70" />
        <span className="text-[12px] font-semibold tracking-wide text-charcoal/80">{label}</span>
      </div>
    ))}
  </motion.div>
);

const HeroCollage = ({ yMain, ySide }) => (
  <div className="relative h-[520px] sm:h-[600px] hidden md:block" data-testid="hero-collage">
    <motion.div
      style={{ y: yMain }}
      initial={MAIN_IMG_HIDDEN}
      animate={IMG_VISIBLE}
      transition={MAIN_IMG_TRANSITION}
      className="absolute inset-x-0 top-[6%] h-[64%] overflow-hidden rounded-t-[120px] rounded-b-xl shadow-[0_30px_60px_rgba(62,74,46,0.2)]"
    >
      <img src={HERO_MAIN} alt="Formulation lab — development, texture work and filling line" className="w-full h-full object-cover" />
    </motion.div>
    <motion.div
      style={{ y: ySide }}
      initial={SIDE_IMG_HIDDEN}
      animate={IMG_VISIBLE}
      transition={SIDE_IMG_TRANSITION}
      className="absolute left-0 bottom-0 w-[38%] h-[38%] overflow-hidden rounded-xl border-4 border-cream shadow-[0_20px_50px_rgba(62,74,46,0.16)]"
    >
      <img src={HERO_SIDE} alt="Luxury serum bottle" className="w-full h-full object-cover" />
    </motion.div>
  </div>
);

export default function Hero({ onContact }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yMain = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const ySide = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative pt-[76px] min-h-screen">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-[1.1fr_0.9fr] gap-16 lg:gap-10 items-center py-16 lg:py-24">
        <div>
          <motion.p
            initial={FADE_HIDDEN}
            animate={FADE_VISIBLE}
            transition={OVERLINE_TRANSITION}
            className="text-xs uppercase tracking-[0.3em] font-semibold text-navy/60 mb-8"
          >
            Formulation &amp; Product Development Studio
          </motion.p>

          <HeroHeadline />

          <motion.p
            initial={RISE_HIDDEN}
            animate={RISE_VISIBLE}
            transition={SUBTITLE_TRANSITION}
            className="mt-8 text-base md:text-lg leading-relaxed text-charcoal/85 max-w-xl"
          >
            Formulation and product development for beauty, wellness and Ayurvedic brands -
            from first idea to market-ready product, guided by Ayurvedic doctors and
            formulation experts.
          </motion.p>

          <HeroCtas onContact={onContact} />
          <HeroBadges />
        </div>

        <HeroCollage yMain={yMain} ySide={ySide} />
      </div>
    </section>
  );
}
